import { makeRequest } from "app/utils/makeRequest";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { USERTYPE } from "app/utils/types";
import { appendAgencyManagerParam } from "app/utils/queryParamHelper";
import {
  ContactProfileResponse,
  ProfileData,
  UserEmailType,
  UserPhoneType,
  UserAddressType,
} from "../components/types";

/**
 * Fetch all contact profile data (emails, phones, addresses, profile)
 */
export const fetchContactProfileData = async (
  contactId: string,
): Promise<ContactProfileResponse> => {
  const response = await makeRequest("contactEmailList").get(contactId, true, false);
  return response.data;
};

/**
 * Helper function to check if current user is an agent
 */
const isAgent = (): boolean => {
  const userAccess = getObjectFromLocalStore("useraccess");
  return userAccess?.userType === USERTYPE.AGENT;
};

/**
 * Helper function to check if current user is an admin
 */
const isAdmin = (): boolean => {
  const userAccess = getObjectFromLocalStore("useraccess");
  return userAccess?.userType === USERTYPE.ADMIN;
};

/**
 * Helper function to check if should use userId query parameter (for agent or admin)
 */
const shouldUseUserIdQuery = (): boolean => isAgent() || isAdmin();

/**
 * Add new email - sends OTP token
 * For agents: adds userId query parameter
 * For non-agents: uses contactId as path parameter
 */
export const addEmailToken = async (
  userId: string,
  emailData: { email: string; type: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
  skipVerify?: boolean,
) => {
  const payload = skipVerify ? { ...emailData, skipVerify: true } : emailData;
  if (agencyId) {
    // For agency context: POST /contact/email/token?agencyId=...
    const response = await makeRequest("contactEmailToken").post(
      payload,
      `agencyId=${agencyId}`,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: POST /contact/email/token?userId=...
    const queryString = `userId=${userId}`;
    const response = await makeRequest("contactEmailToken").post(payload, queryString, false, true);
    return response.data;
  }
  // For non-agents/admins: POST /contact/email/token
  const response = await makeRequest("contactEmailToken").post(payload, undefined, false, false);
  return response.data;
};

/**
 * Update email - sends OTP token
 * Works for both agent and non-agent users
 * Body should contain: { email: string, type: string }
 */
export const updateEmailToken = async (
  userEmailId: string,
  emailData: { email: string; type: string },
  skipVerify?: boolean,
  userId?: string,
) => {
  const payload = skipVerify ? { ...emailData, skipVerify: true } : emailData;
  // PUT /contact/email/token/{userEmailId}, or /{userEmailId}?userId=... for advisor updates
  const key = userId ? `${userEmailId}?userId=${userId}` : userEmailId;
  const response = await makeRequest("contactEmailToken").put(key, payload, true, false);
  return response.data;
};

/**
 * Verify email with OTP
 * For agents: adds userId query parameter
 * For non-agents: uses contactId as path parameter
 */
export const verifyEmail = async (
  id: string,
  verifyData: { email: string; verifyToken: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: POST /contact/email/verify?agencyId=...
    const response = await makeRequest("contactEmailVerify").post(
      verifyData,
      `agencyId=${agencyId}`,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: POST /contact/email/verify?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactEmailVerify").post(
      verifyData,
      queryString,
      false,
      true,
    );
    return response.data;
  }
  // For non-agents/admins: POST /contact/email/verify
  const response = await makeRequest("contactEmailVerify").post(
    verifyData,
    undefined,
    false,
    false,
  );
  return response.data;
};

/**
 * Set primary email
 * For agents: adds userId query parameter
 * Body: { email: string, userEmailId: string }
 */
export const setPrimaryEmail = async (
  userId: string,
  primaryData: { email: string; userEmailId: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: PUT /contact/email/primary?agencyId=...
    const response = await makeRequest("contactEmailPrimary").put(
      `agencyId=${agencyId}`,
      primaryData,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: PUT /contact/email/primary?userId=...
    const queryString = `userId=${userId}`;
    const response = await makeRequest("contactEmailPrimary").put(
      queryString,
      primaryData,
      false,
      true,
    );
    return response.data;
  }
  // For non-agents/admins: PUT /contact/email/primary
  const response = await makeRequest("contactEmailPrimary").put(
    undefined,
    primaryData,
    false,
    false,
  );
  return response.data;
};

/**
 * Resend email OTP token
 * Works for both agent and non-agent users
 * Body: { email: string, userEmailId: string }
 */
export const resendEmailToken = async (resendData: { email: string; id?: string }) => {
  const response = await makeRequest("contactEmailResendOtp").post(
    resendData,
    undefined,
    false,
    false,
  );
  return response.data;
};

/**
 * Verify primary email with OTP
 * For agents: adds userId query parameter
 * For non-agents: uses contactId
 */
export const verifyPrimaryEmail = async (
  id: string,
  verifyData: { email: string; verifyToken: string; userEmailId: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: PUT /contact/email/primary/verify?agencyId=...
    const response = await makeRequest("contactEmailVerifyPrimary").put(
      `agencyId=${agencyId}`,
      verifyData,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: PUT /contact/email/primary/verify?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactEmailVerifyPrimary").put(
      queryString,
      verifyData,
      false,
      true,
    );
    return response.data;
  }
  // For non-agents/admins: PUT /contact/email/primary/verify
  const response = await makeRequest("contactEmailVerifyPrimary").put(
    undefined,
    verifyData,
    false,
    false,
  );
  return response.data;
};

/**
 * Delete email
 */
export const deleteEmail = async (id: string) => {
  const response = await makeRequest("contactEmailDelete").delete(id, true, false);
  return response.data;
};

/**
 * Add new phone - sends OTP token
 * For agents: adds userId query parameter
 * For non-agents: simple POST body (no path / query)
 *
 * 1. Add phone for contact:
 *    POST /contact/phone/token
 *    body: { countryCode, phoneNumber, type }
 *
 * 2. Add phone for agent:
 *    POST /contact/phone/token?userId={userId}
 *    body: { countryCode, phoneNumber, type }
 */
export const addPhoneToken = async (
  id: string,
  phoneData: { countryCode: string; phoneNumber: string; type: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
  skipVerify?: boolean,
) => {
  const payload = skipVerify ? { ...phoneData, skipVerify: true } : phoneData;
  if (agencyId) {
    // For agency context: POST /contact/phone/token?agencyId=...
    const response = await makeRequest("contactPhoneToken").post(
      payload,
      `agencyId=${agencyId}`,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: POST /contact/phone/token?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactPhoneToken").post(payload, queryString, false, true);
    return response.data;
  }

  // For non-agents/admins (contact): POST /contact/phone/token
  const response = await makeRequest("contactPhoneToken").post(payload, undefined, false, false);
  return response.data;
};

/**
 * Update phone - sends OTP token
 *
 * 5. Update phone for agent and contact:
 *    PUT /contact/phone/token/{userPhoneId}
 *    body: { countryCode, phoneNumber, type }
 */
export const updatePhoneToken = async (
  id: string,
  phoneData: { countryCode: string; phoneNumber: string; type: string },
  skipVerify?: boolean,
  userId?: string,
) => {
  const payload = skipVerify ? { ...phoneData, skipVerify: true } : phoneData;
  // PUT /contact/phone/token/{id}, or /{id}?userId=... for advisor updates
  const key = userId ? `${id}?userId=${userId}` : id;
  const response = await makeRequest("contactPhoneToken").put(key, payload, true, false);
  return response.data;
};

/**
 * Verify phone with OTP
 *
 * 3. Verify phone for contact:
 *    POST /contact/phone/verify
 *    body: { countryCode, phoneNumber, verifyToken }
 *
 * 4. Verify phone for agent:
 *    POST /contact/phone/verify?userId={userId}
 *    body: { countryCode, phoneNumber, verifyToken }
 */
export const verifyPhone = async (
  id: string,
  verifyData: { countryCode: string; phoneNumber: string; verifyToken: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: POST /contact/phone/verify?agencyId=...
    const response = await makeRequest("contactPhoneVerify").post(
      verifyData,
      `agencyId=${agencyId}`,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: POST /contact/phone/verify?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactPhoneVerify").post(
      verifyData,
      queryString,
      false,
      true,
    );
    return response.data;
  }

  // For non-agents/admins (contact): POST /contact/phone/verify
  const response = await makeRequest("contactPhoneVerify").post(
    verifyData,
    undefined,
    false,
    false,
  );
  return response.data;
};

/**
 * Delete phone
 */
export const deletePhone = async (id: string) => {
  const response = await makeRequest("contactPhoneDelete").delete(id, true, false);
  return response.data;
};

/**
 * Set primary phone
 *
 * 6. Set primary phone for agent:
 *    PUT /contact/phone/primary?userId={userId}
 *    body: { countryCode, phoneNumber, userPhoneId }
 *
 * 7. Set primary phone for contact:
 *    PUT /contact/phone/primary
 *    body: { countryCode, phoneNumber, userPhoneId }
 */
export const setPrimaryPhone = async (
  id: string,
  primaryData: { countryCode: string; phoneNumber: string; userPhoneId: string },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: PUT /contact/phone/primary?agencyId=...
    const response = await makeRequest("contactPhonePrimary").put(
      `agencyId=${agencyId}`,
      primaryData,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: PUT /contact/phone/primary?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactPhonePrimary").put(
      queryString,
      primaryData,
      false,
      true,
    );
    return response.data;
  }

  // For non-agents/admins (contact): PUT /contact/phone/primary
  const response = await makeRequest("contactPhonePrimary").put(
    undefined,
    primaryData,
    false,
    false,
  );
  return response.data;
};

/**
 * Resend phone OTP token
 *
 * 8. Contact and agent resend OTP:
 *    POST /contact/phone/resend-token
 *    body: { countryCode, phoneNumber, id }
 */
export const resendPhoneToken = async (resendData: {
  countryCode: string;
  phoneNumber: string;
  id?: string;
}) => {
  const response = await makeRequest("contactPhoneResendOtp").post(
    resendData,
    undefined,
    false,
    false,
  );
  return response.data;
};

/**
 * Verify primary phone with OTP
 * For agents: adds userId query parameter
 * For non-agents: uses contactId
 */
export const verifyPrimaryPhone = async (
  id: string,
  verifyData: {
    countryCode: string;
    phoneNumber: string;
    verifyToken: string;
    userPhoneId: string;
  },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: PUT /contact/phone/primary/verify?agencyId=...
    const response = await makeRequest("contactPhoneVerifyPrimary").put(
      `agencyId=${agencyId}`,
      verifyData,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: PUT /contact/phone/primary/verify?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactPhoneVerifyPrimary").put(
      queryString,
      verifyData,
      false,
      true,
    );
    return response.data;
  }
  // For non-agents/admins: PUT /contact/phone/primary/verify
  const response = await makeRequest("contactPhoneVerifyPrimary").put(
    undefined,
    verifyData,
    false,
    false,
  );
  return response.data;
};

/**
 * Add new address
 *
 * 1. Add address for contact:
 *    POST /contact/address
 *    body: { address, city, state, country, zipCode, type, isPrimary }
 *
 * 2. Add address for agent:
 *    POST /contact/address?userId={userId}
 *    body: { address, city, state, country, zipCode, type, isPrimary }
 */
export const addAddress = async (
  id: string,
  addressData: {
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    type: string;
    isPrimary: boolean;
  },
  useUserIdQuery?: boolean,
  agencyId?: string,
) => {
  if (agencyId) {
    // For agency context: POST /contact/address?agencyId=...
    const response = await makeRequest("contactAddressCreate").post(
      addressData,
      `agencyId=${agencyId}`,
      false,
      true,
    );
    return response.data;
  }
  const shouldUseQuery = useUserIdQuery !== undefined ? useUserIdQuery : shouldUseUserIdQuery();
  if (shouldUseQuery) {
    // For agents/admins: POST /contact/address?userId=...
    const queryString = `userId=${id}`;
    const response = await makeRequest("contactAddressCreate").post(
      addressData,
      queryString,
      false,
      true,
    );
    return response.data;
  }

  // For contacts: POST /contact/address
  const response = await makeRequest("contactAddressCreate").post(
    addressData,
    undefined,
    false,
    false,
  );
  return response.data;
};

/**
 * Update address for contact and agent
 *
 * 3. Update address:
 *    PUT /contact/address/{addressId}
 *    body: { address, city, state, country, zipCode, type, isPrimary }
 *
 * 4. Update address for agency context:
 *    PUT /contact/address/{addressId}?agencyId={agencyId}
 *
 * Note: Setting primary is also handled via this API using the "isPrimary" flag.
 */
export const updateAddress = async (
  _contactId: string,
  addressId: string,
  addressData: {
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    type: string;
    isPrimary: boolean;
  },
  agencyId?: string,
) => {
  const key = agencyId ? `${addressId}?agencyId=${agencyId}` : addressId;
  const response = await makeRequest("contactAddressCreate").put(key, addressData, true, false);
  return response.data;
};

/**
 * Delete address
 */
export const deleteAddress = async (id: string) => {
  const response = await makeRequest("contactAddressCreate").delete(id, true, false);
  return response.data;
};

/**
 * Update profile (first name, last name, date of birth, profile picture)
 */
export const updateProfile = async (contactId: string, profileData: Partial<ProfileData>) => {
  const queryParam = appendAgencyManagerParam();
  const response = await makeRequest("contactPersonalInfoEdit").put(
    `${contactId}${queryParam}`,
    profileData,
    true,
    false,
  );
  return response.data;
};

/**
 * Upload profile picture
 */
export const uploadProfilePicture = async (contactId: string, file: File) => {
  const formData = new FormData();
  formData.append("profilePicture", file);
  formData.append("access", "public-read");
  const response = await makeRequest("profilePictureUpload", undefined, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).post(formData, contactId, true, false);
  return response.data;
};

/**
 * Delete profile picture
 */
export const deleteProfilePicture = async (contactId: string) => {
  const response = await makeRequest("profilePictureDelete").delete(contactId, true, false);
  return response.data;
};

/**
 * Helper function to map API email type to display label
 */
export const mapEmailTypeToLabel = (type: string): "Work" | "Personal" | "Other" => {
  switch (type?.toUpperCase()) {
    case UserEmailType.WORK:
      return "Work";
    case UserEmailType.PERSONAL:
      return "Personal";
    case UserEmailType.OTHER:
      return "Other";
    default:
      return "Work";
  }
};

/**
 * Helper function to map display label to API email type
 */
export const mapLabelToEmailType = (label: "Work" | "Personal" | "Other"): string => {
  switch (label) {
    case "Work":
      return UserEmailType.WORK;
    case "Personal":
      return UserEmailType.PERSONAL;
    case "Other":
      return UserEmailType.OTHER;
    default:
      return UserEmailType.WORK;
  }
};

/**
 * Helper function to map API phone type to display label
 */
export const mapPhoneTypeToLabel = (type: string): "Mobile" | "Office" | "Other" => {
  switch (type?.toUpperCase()) {
    case UserPhoneType.MOBILE:
      return "Mobile";
    case UserPhoneType.OFFICE:
      return "Office";
    case UserPhoneType.OTHER:
      return "Other";
    default:
      return "Mobile";
  }
};

/**
 * Helper function to map display label to API phone type
 */
export const mapLabelToPhoneType = (label: "Mobile" | "Office" | "Other"): string => {
  switch (label) {
    case "Mobile":
      return UserPhoneType.MOBILE;
    case "Office":
      return UserPhoneType.OFFICE;
    case "Other":
      return UserPhoneType.OTHER;
    default:
      return UserPhoneType.MOBILE;
  }
};

/**
 * Helper function to map API address type to display label
 */
export const mapAddressTypeToLabel = (type: string): "Home" | "Work" | "Other" => {
  switch (type?.toUpperCase()) {
    case UserAddressType.HOME:
      return "Home";
    case UserAddressType.WORK:
      return "Work";
    case UserAddressType.OTHER:
      return "Other";
    default:
      return "Home";
  }
};

/**
 * Helper function to map display label to API address type
 */
export const mapLabelToAddressType = (label: "Home" | "Work" | "Other"): string => {
  switch (label) {
    case "Home":
      return UserAddressType.HOME;
    case "Work":
      return UserAddressType.WORK;
    case "Other":
      return UserAddressType.OTHER;
    default:
      return UserAddressType.HOME;
  }
};
